/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useEffect, useMemo, useState} from 'react'
import {removeQueryParamsFromPath} from '@salesforce/retail-react-app/app/utils/url'
import {useHistory, useLocation} from 'react-router-dom'
import {useVariant} from '@salesforce/retail-react-app/app/hooks/use-variant'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getSelectedStyleGroupingProductId, isStyleGroupingActive} from '../utils/style-grouping'

/**
 * This hook is responsible for fetching a product detail based on the variation selection
 * and managing the variation params on the url when the modal is open/close
 * @param initialProduct - the initial product when the modal is first open
 * @returns object
 */
export const useProductViewModal = (initialProduct) => {
    const location = useLocation()
    const history = useHistory()
    const intl = useIntl()
    const toast = useToast()
    const normalizedInitialProduct = useMemo(
        () => filterVariantsWithoutVariationValues(initialProduct),
        [initialProduct]
    )
    const [product, setProduct] = useState(normalizedInitialProduct)
    const variant = useVariant(product)

    const selectedStyleGroupingProductId = useMemo(
        () => getSelectedStyleGroupingProductId(normalizedInitialProduct, location.search),
        [location.search, normalizedInitialProduct]
    )
    const productIdToFetch = isStyleGroupingActive(normalizedInitialProduct)
        ? variant?.productId || selectedStyleGroupingProductId || product?.productId || product?.id
        : variant?.productId || product?.productId || product?.id

    const {data: currentProduct, isFetching} = useProduct(
        {
            parameters: {
                id: productIdToFetch,
                allImages: true
            }
        },
        {
            placeholderData: normalizedInitialProduct,
            select: (data) => {
                // if the product id is the same as the initial product id,
                // then merge the data with the initial product to be able to show correct quantity in the modal
                if (data.id === normalizedInitialProduct.productId) {
                    return {
                        ...normalizedInitialProduct,
                        ...data
                    }
                }
                if (isStyleGroupingActive(normalizedInitialProduct)) {
                    return {
                        ...normalizedInitialProduct,
                        ...data,
                        c_associatedVariationGroupIds:
                            normalizedInitialProduct.c_associatedVariationGroupIds,
                        c_associatedVariationAttributes:
                            normalizedInitialProduct.c_associatedVariationAttributes,
                        variationAttributes: data.variationAttributes?.length
                            ? data.variationAttributes
                            : normalizedInitialProduct.variationAttributes
                    }
                }
                return data
            },
            onError: () => {
                toast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }
        }
    )

    useEffect(() => {
        if (currentProduct && !isFetching) {
            const filteredProduct = filterVariantsWithoutVariationValues(currentProduct)
            setProduct(filteredProduct)
        }
    }, [currentProduct, isFetching])

    const cleanUpVariantParams = () => {
        const paramToRemove = [...(product?.variationAttributes?.map(({id}) => id) ?? []), 'pid']
        const updatedParams = removeQueryParamsFromPath(`${location.search}`, paramToRemove)

        history.replace({search: updatedParams})
    }

    useEffect(() => {
        // when the modal is first mounted,
        // clean up the params in case there are variant params not related to current product
        cleanUpVariantParams()
        return () => {
            cleanUpVariantParams()
        }
    }, [])

    return {
        product,
        variant,
        isFetching,
        initialProduct: normalizedInitialProduct
    }
}

function filterVariantsWithoutVariationValues(productObject) {
    return {
        ...productObject,
        variants:
            productObject.variants?.filter(
                (variant) =>
                    variant.variationValues && Object.keys(variant.variationValues).length > 0
            ) || []
    }
}
